import { Component } from '@angular/core';
import { BASIC_IMPORTS } from '../../../imports/common-imports';
import { CalendarService } from '../../../services/calendar.service';
import { FilterStateService } from '../../../services/filter-state.service';

@Component({
  selector: 'app-monthly',
  standalone: true,
  imports: [BASIC_IMPORTS],
  templateUrl: './monthly.component.html',
  styleUrl: './monthly.component.scss',
})
export class MonthlyComponent {
  monthLabels = CalendarService.monthLabels;
  months: { month: string; year: number; selected: boolean }[] = [];
  sessionCacheKey = 'monthly-selected-range';

  constructor(
    private readonly filterStateService: FilterStateService,
    private readonly calendarService: CalendarService,
  ) {}

  ngOnInit() {
    const lastDate = this.calendarService.lastDate!;
    const firstDate = this.calendarService.firstDate!;

    const lastMonth = lastDate.month;
    const lastYear = lastDate.year;
    const firstMonth = firstDate.month;
    const firstYear = firstDate.year;

    for (let year = firstYear; year <= lastYear; year++) {
      const startMonth = year === firstYear ? firstMonth : 1;
      const endMonth = year === lastYear ? lastMonth : 12;

      for (let month = startMonth; month <= endMonth; month++) {
        const monthName = this.monthLabels[month - 1];
        this.months.push({
          month: monthName,
          year,
          selected: false,
        });
      }
    }

    // load selected months from session storage
    const cachedData = sessionStorage.getItem(this.sessionCacheKey);
    if (cachedData) {
      const selectedRange = JSON.parse(cachedData) as {
        month: string;
        year: number;
        selected: boolean;
      }[];

      this.months.forEach((m) => {
        const isSelected = selectedRange.find(
          (sel) => sel.month === m.month && sel.year === m.year,
        );
        if (isSelected) {
          m.selected = true;
        }
      });
    }
  }

  getMonthIndex(month: string): string {
    const monthIdx = this.monthLabels.indexOf(month) + 1;

    return monthIdx < 10 ? `0${monthIdx}` : `${monthIdx}`;
  }

  handleClick(month: any, idx: number) {
    const countSelected = this.months.filter((m) => m.selected === true).length;

    if (countSelected === 0) month.selected = !month.selected;

    if (countSelected === 1) {
      let firstSelectedIdx = this.months.findIndex((m) => m.selected === true);

      //if selected month is before the first selected, select it and set it as the first
      if (idx < firstSelectedIdx) {
        this.months[idx].selected = true;
        // switch fist selected and idx
        const temp = firstSelectedIdx;
        firstSelectedIdx = idx;
        idx = temp;
      }

      // select every month in between
      for (let i = firstSelectedIdx; i < this.months.length; i++) {
        if (i <= idx) {
          this.months[i].selected = true;
        }
      }
    } else if (countSelected >= 2) {
      // reset all selections
      this.months.forEach((m) => (m.selected = false));
      month.selected = !month.selected;
    }
  }

  isFirstOrLast(idx: number): boolean {
    const selectedMonths = this.months.filter((m) => m.selected);

    if (selectedMonths.length < 2) return false;

    const firstSelected = selectedMonths[0];
    const lastSelected = selectedMonths[selectedMonths.length - 1];

    return (
      this.months[idx] === firstSelected || this.months[idx] === lastSelected
    );
  }

  saveData() {
    let selectedRange = this.months.filter((m) => m.selected);

    //cache user selected range in session storage
    sessionStorage.setItem(this.sessionCacheKey, JSON.stringify(selectedRange));

    if (selectedRange.length === 0) {
      sessionStorage.removeItem(this.sessionCacheKey); // clear cache if no selection
      selectedRange = [...this.months];
      // Clear date filter keys when no selection
      this.filterStateService.clearFilter('date_range');
    } else {
      // Track applied date filter key
      this.filterStateService.applyFilter('date_range');
    }

    const startMonth = selectedRange[0];
    const endMonth = selectedRange[selectedRange.length - 1];

    const startMonthStr = `${startMonth.year}-${this.getMonthIndex(
      startMonth.month,
    )}`;
    const endMonthStr = `${endMonth.year}-${this.getMonthIndex(
      endMonth.month,
    )}`;

    this.filterStateService.filterData.start_month = startMonthStr;
    this.filterStateService.filterData.end_month = endMonthStr;
  }

  clear() {
    this.months.forEach((m) => (m.selected = false));
  }
}
